/**
 * Map with per-key time-to-live. Each `set` arms a `setTimeout` that deletes
 * the entry when the TTL elapses. Used by {@link PrivateEventBus} for dedup
 * keys whose lifetime is bounded.
 *
 * Not thread-safe; designed for the browser event loop.
 */
export class TtlMap<K, V> {
  private readonly entries = new Map<K, V>();
  private readonly timers = new Map<K, ReturnType<typeof setTimeout>>();

  set(key: K, value: V, ttlMs: number): void {
    this.clearTimer(key);
    this.entries.set(key, value);
    const timer = setTimeout(() => {
      this.timers.delete(key);
      this.entries.delete(key);
    }, ttlMs);
    this.timers.set(key, timer);
  }

  get(key: K): V | undefined {
    return this.entries.get(key);
  }

  has(key: K): boolean {
    return this.entries.has(key);
  }

  delete(key: K): void {
    this.clearTimer(key);
    this.entries.delete(key);
  }

  private clearTimer(key: K): void {
    const t = this.timers.get(key);
    if (t !== undefined) {
      clearTimeout(t);
      this.timers.delete(key);
    }
  }
}
